/**
 * @fileoverview contains all feature flags used in the frontend of the application.
 *
 * Feature flag keys must be added to this file in one of the arrays {@link featureFlagKeys}, {@link adminFeatureKeys}, or {@link featureKeys} or they will
 * not be accessible on the frontend.
 */

import type { UserFeature } from 'venn-api-client/public_api/misc/v1/feature_availability_pb';
import { assert } from '../utils';

type FeatureFlagKey = (typeof featureFlagKeys)[number];

/**
 * Temporary feature flags.
 * Please append these with _ff to make distinguishable from features used for pricing.
 */
export const featureFlagKeys = [
  'allow_leverage_short_ff', // ff to allow leverage and shorting for portfolios
  'analysis_export_watermark_ff', // ff for forcing a watermark on analysis page print-outs
  'add_investment_redesign_ff', // ff for rollout of redesigned add investment experience
  'block_assignment_ff', // ff for VER-525: multi-block selection in Studio/RL
  'canvas_heatmap_ff', // ff for enabling faster-rendering heatmaps using canvas
  'compare_export_watermark_ff', // ff for forcing a watermark on compare page print-outs
  'compliance_metadata_export_ff', // ff to send compliance metadata with finalized Report Lab exports
  'date_range_in_data_ff', // flag to show pagination in data range
  'default_rl_advisory_fees_ff', // ff to enable workspace-level advisory fee configuration in Report Lab
  'disable_exports_ff', // ff for disabling the ability to export, if the user does not have report lab
  'enable_backfill_quarterly_ff', // ff to enable backfilling for quarterly investments
  'enable_intercom_ff', // ff to enable the Intercom widget
  'enable_interpolation_monthly_ff', // ff to enable interpolation for monthly investments
  'extrapolation_ff', // ff for enabling extrapolation configuration
  'full_access_seat_cap_rollout_ff', // ff for showing the users their progress towards the seatcap limit
  // TODO: remove for Jira VENN-29581
  'historicals_portfolio_contribution_ff', // ff to support historicals portfolio contributions in Studio/RL
  'holding_proxy_enhancements_ff', // ff for "Holdings Proxy"  all the remaining work
  'holding_proxy_ff', // ff to show “Holdings Proxy” column on Manage Data page
  'import_export_strategies_ff', // Temporary flag for importing portfolios as strategies to a portfolio / saving strategies as portfolios
  'advisory_fees_for_rl_investments_ff', // ff to enabling user to add advisory fees to investments in report lab
  'layered_proxy_picker_ff', // ff for enabling tabbed UI for layered proxy configuration (backfill + transformations)
  'macro_scenario_ff', // ff for enabling macroeconomic scenarios in scenario analysis on studio/report lab
  'mfa_updates_ff', // ff to enable new MFA changes
  'modern_layout_no_revert_ff', // ff for disabling the ability to revert back to the legacy layout in report lab
  'my_data_page_ff', // ff for My Data view (hide, show)
  'non_primary_share_class_ff', // Flag for showing additional share classes of mutual funds
  'portfolio_filtering_ff', // ff for VER-923: portfolio filtering in Studio/RL
  'rcm_rl_customization_ff', // ff for enabling RCM customization features in Studio/Report Lab like Inter font instead of Roboto
  'rcm_strategy_colors_ff', // ff for enabling RCM custom strategy colors in Portfolio Composition block
  'rcm_strategy_enforcement_ff', // ff for Disabling strategy for RCM users
  'report_lab_export_watermark_ff', // ff for forcing a watermark on report lab print-outs
  'restrict_cma_forecast_ff', // Temporary flag to limit wealth managers from using CMA forecasts
  'studio_export_watermark_ff', // ff for forcing a watermark on studio page print-outs
  'studio_venncast_ff', // Temporary flag to disable venncast in studio till it is ready
  'swiss_disclosure_ff', // ff for showing Swiss securities disclaimer in RL/Studio among default Venn disclosures
  'tax_impact_ff', // ff for enabling tax impact analysis features
  'test_hide_category_ff', // Test flag for hiding categories on Analysis Page & checking if anyone missed them
  'virtualization_ff', // ff for enabling virtualization of scrolling in studio and report lab
  'yield_income_ff', // ff for enabling user to be able to enter and see custom yield metrics
  'rcm_portfolio_strategy_enforcement_ff', // ff for disabling strategy enforcement in portfolio allocator for RCM users
  'rl_customization_enhancements_ff', // ff for rollout of enhanced rl customization options, such as full page images
  'investment_upload_redesign_ff', // ff for rollout of redesigned investment upload experience
  'upsell_holdings_ff', // ff for rollout of upsell flyout for holdings + new button group in left menu
  'venn_daily_private_asset_returns_ff', // ff for enabling daily private asset returns asset type
  'venn_daily_private_asset_returns_marketing_ff', // ff for showing VDPAR marketing copy in upsell/homepage
  'investment_ownership_ff', // ff for rollout of new permissions for investments
  'rcm_manage_default_endpoint_ff', // ff to change RCM default entry point of portfolios from Analysis Page to Manage Data Page
  'skip_k_suffix_in_formatting_ff', // ff to skip the k suffix in formatting of $ numbers
  'ask_venn_ff', // ff for Ask Venn AI chat panel
  'block_multi_pages', // ff for block spans multiple pages
  'portfolio_lab_ff', // ff for portfolio lab redesign (header + sidebar restyle)
  'data_library_persist_selection_ff', // ff for persistence of library selection across pages
  'venn_help_on_riskpro_ff', // ff to serve Venn help center articles on the Risk Pro brand instead of the Solovis ones
] as const;

type AdminFeature = (typeof adminFeatureKeys)[number];
/** Admin features. */
export const adminFeatureKeys = [
  'admin_workspace_switch', // User has access to organization switch (Venn system admins only)
  'admin_data_reader', // User is a venn team member with access to read admin data
] as const;

type FeatureKey = (typeof featureKeys)[number];
/** More permanent features, such as used for pricing. Should not end in _ff. */
export const featureKeys = [
  'absolute_path', // Feature used for debugging tests for absolute path to feature service
  'addepar_self_service', // Users who are pre-cleared to use the Addepar self-service functionality
  'admin_switch_organizations', // Not really an admin feature just enables a searchable workspace switcher
  'canada_restriction', // Canadian users who should be restricted from accessing stocks and other functionality
  'categories_search_library_ff', // Show morningstar categories filter on the library page directly
  'color_scheme_picker', // Enables a picker which changes color settings for the entire workspace when saved
  'composite_benchmarks_visibility_ff', // Whether a user can set an ownership context to a composite benchmark
  'context_switching', // Ability to switch contexts within an organisation
  'cookie_preferences', // Easy way for devs not in EU to check the GDPR cookie preferences banner
  'custom_peer_groups_ff', // Enable stored searches as peer groups
  'frontend_debug_panel', // Show the debug panel in the frontend
  'frontend_driven_downtime',
  'full_access_seat_cap', // Read the limit on the number of users in organization, OR if this feature is not present (i.e. the limit is 0) used to hide the ability to invite others to the workspace
  'hide_venn_provided_items_ff', // Hides Venn-created templates and demo portfolio
  'historical_portfolios_ff', // enable viewing historical portfolios
  'holdings_metadata', // Enabled the new holdings metadata blocks in studio/report lab
  'holdings_metadata_new_fixed_income', // Enables new fixed income features (Muni breakdown, Modified Duration FPI metric)
  'holdings_metadata_factset_fixed_income', // Enables FactSet fixed income metrics (Tax Equivalent Yield and Spread Duration FPI metrics)
  'individual_benchmarks_visibility_ff', // If the current account have access to setting individual benchmark visibility
  'new_permissions_ff', // If the current account uses the new WM permissions system
  'new_rebalance_ff', // adds support for the NO_REBALANCE and CUSTOM_REBALANCE options
  'optimization', // User has access to optimization (pro & trial users except certain countries)
  'options', // Enables options analytics functionality
  'original_ais_org', // used to prevent onboarding video showing for existing users
  'private_analytics', // enable private analytics experience within the application
  'proxy_visibility_ff', // If the current account have access to guarding their proxy visibility
  'sponsor',
  'studio_report_editor', // Ability to access the report editing functionality in studio
  'studio_use_parallel_queries_ff', // enable parallel queries with useQueries in Studio/Report Lab
  'venn_branded_watermark_ff', // Whether to show the Venn branded watermark on blocks in non-RL modules.
  'wealth_management_organization', // Whether an organization is a wealth management organization, affects disclaimers and disclosures
  'workspace_admin_actions', // User can remove teammates from workspace, revoke invitations and modify roles
  'yield_income_ff', // Visibility of yield metrics and metadata
  'multilevel_portfolio_filtering_ff', // allow for multi-level portfolio filtering in Studio/RL for portfolio contribution tables and charts
] as const;

/** Union of all possible feature keys or names. */
export type FeatureName = FeatureKey | FeatureFlagKey | AdminFeature;
export type PathType = 'RELATIVE_PATH' | 'ABSOLUTE_PATH';

/**
 * Ensures all keys in the provided object are lowercase at both compile time and runtime.
 * We use this because feature keys are normalized to lowercase to prevent casing issues.
 *
 * Alternative considered: the TS4.9 satisfies operator (satisfies Lowercase<string>) would be a clean compile-time solution,
 * but it doesn't do any runtime check and we don't have TS4.9 yet.
 */
function checkLowercaseKeys(arr: readonly Lowercase<string>[]) {
  arr.forEach((v) => assert(v.toLowerCase() === v, `expected ${v} to be lowercase`));
}
checkLowercaseKeys(featureFlagKeys);
checkLowercaseKeys(adminFeatureKeys);
checkLowercaseKeys(featureKeys);

export type FeatureFlagRecord = { [_name in FeatureName]?: UserFeature };
export type PartialFeatureFlagRecord = { [_name in FeatureName]?: Partial<UserFeature> };
